import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { Fragment, ReactNode, useState } from 'react';
import { FormattedDate } from 'react-intl';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import Collapse from 'atoms/layout/Collapse';
import { ContentWithStickyHeader } from 'atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from 'atoms/layout/FullWidthContainer';
import { Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';

type Props = {
  date: Date;
  nodes: ReactNode[];
  selected?: boolean;
  highlighted?: boolean;
  collapsible?: boolean;
  initialExpanded?: boolean;
  onCollapse?: () => void;
};

const StyledContentWithStickyHeader = styled(ContentWithStickyHeader)<{
  noBorder?: boolean;
}>`
  ${({ noBorder }) =>
    !noBorder &&
    css`
      border-bottom: 1px solid var(--c-neutral-200);
    `}
`;

const Header = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--unit) var(--quadruple-unit) var(--unit) var(--triple-unit);
  gap: var(--double-unit);
  width: 100%;
  background: var(--c-neutral-100);
`;

const Icon = styled(FontAwesomeIcon)<{ $expanded: boolean }>`
  color: var(--c-static-neutral-500);
  fill: var(--c-neutral-600);
  transform: rotate(0deg);
  transition: 0.3s transform;

  ${({ $expanded }) =>
    $expanded &&
    css`
      transform: rotate(-180deg);
    `}
`;

const Items = styled(Vertical).attrs({ gap: 2 })`
  padding: 2px 0 var(--double-unit);
  isolation: isolate;
`;

const CalendarItem = ({
  date,
  nodes,
  selected,
  highlighted,
  collapsible: collapsibleProp,
  initialExpanded = true,
  onCollapse,
}: Props) => {
  const collapsible = collapsibleProp || nodes.length === 0;

  const [expanded, setExpanded] = useState(initialExpanded);

  if (!expanded && (selected || highlighted)) {
    setExpanded(true);
  }

  const handleToggle = () => {
    if (!collapsible) return;

    // Ancestors change selected or highlighted index when collapsing a date
    // with a selected or highlighted node; allow a tick for the change to
    // happen to prevent the date from re-expanding selected/highlighted.
    onCollapse?.();
    setTimeout(() => setExpanded(value => !value));
  };

  return (
    <StyledContentWithStickyHeader
      noBorder={expanded}
      header={
        <FullWidthContainer.NoPadding>
          <Header disabled={!collapsible} onClick={handleToggle}>
            <BodyM bold as="div">
              <FormattedDate
                value={date}
                day="numeric"
                month="long"
                weekday="long"
              />
            </BodyM>
            {collapsible && <Icon icon={faChevronDown} $expanded={expanded} />}
          </Header>
        </FullWidthContainer.NoPadding>
      }
    >
      <Collapse open={expanded || !collapsible} hideGap>
        <Items>
          {nodes.map((node, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={index}>{node}</Fragment>
          ))}
        </Items>
      </Collapse>
    </StyledContentWithStickyHeader>
  );
};

export default CalendarItem;
